import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/useToast'
import {
  trackClick,
  saveAttributionToStorage,
  type ReferralAttribution,
} from '@/lib/api/referralClientApi'

/**
 * Mounted once at the App root. On every route change we look for:
 *   - `?ref=CODE` / `?code=CODE`  → record click, stash attribution, scrub the URL.
 *
 * `/r/:slug` is handled by a dedicated route component (see `ReferralLinkRedirect`)
 * because it needs to resolve the destination server-side before rendering.
 *
 * Design notes:
 *  - The click is fire-and-forget: we never block rendering on the API.
 *  - URL is cleaned via `history.replaceState` so the ref code doesn't leak into
 *    bookmarks, analytics, or social shares.
 *  - Each (pathname + search) is processed at most once per mount via a ref guard.
 */
export function ReferralLandingHandler() {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const processedKey = useRef<string | null>(null)

  useEffect(() => {
    // Guard: each unique URL combination is only processed once.
    const key = location.pathname + location.search
    if (processedKey.current === key) return
    processedKey.current = key

    const params = new URLSearchParams(location.search)
    const code = params.get('ref') || params.get('code')
    if (!code) return

    // Non-blocking: fire the tracking call, handle toast + storage when it resolves.
    trackClick(code)
      .then((res) => {
        const attribution: ReferralAttribution = {
          code: res.code ?? code,
          slug: res.slug ?? null,
          influencerName: res.influencerName,
          studentDiscountPercent: res.studentDiscountPercent,
          expiresAt: res.attributionExpiresAt,
        }
        saveAttributionToStorage(attribution)

        const who = res.influencerName || 'a friend'
        const discount = res.studentDiscountPercent
        const message =
          discount && discount > 0
            ? `You're using ${who}'s referral! Get ${discount}% off.`
            : `You're using ${who}'s referral!`
        toast('success', message, 6000)
      })
      .catch((err) => {
        // Silent failure on bad codes — we don't want to scare a legitimate visitor
        // who pasted a junk link. Log for diagnostics only.
        console.warn('Referral tracking failed:', err)
      })

    // Strip the ref/code query params from the URL without adding a history entry.
    // Keep any other params the page might care about.
    params.delete('ref')
    params.delete('code')
    const remaining = params.toString()
    const cleanUrl =
      location.pathname + (remaining ? `?${remaining}` : '') + location.hash
    // Use navigate with replace:true so React Router state stays in sync.
    navigate(cleanUrl, { replace: true })
  }, [location.pathname, location.search, location.hash, navigate, toast])

  return null
}

export default ReferralLandingHandler
